import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Leaf, Hourglass, Play, Lock, FileDown, RotateCcw, Sprout } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BACKEND, remaining } from "@/lib/ceda";
import { CapsuleFiche, CapsulePosologie } from "@/components/ceda/CapsuleSheet";

export default function MesCeda() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/ceda/me").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { if (user) load(); }, [user]);

  const activate = async (item) => {
    try {
      await api.post(`/ceda/me/${item.id}/activate`);
      toast.success("Capsule activée — vos 7 jours commencent");
      setConfirm(null); setOpen(item.id); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (user === false) return <Gate />;
  if (!data) return <p className="max-w-4xl mx-auto px-4 py-24 font-sans text-[#1F2A24]/60">Chargement…</p>;
  const groups = ["active", "pending", "expired"].map((s) => [s, data.items.filter((i) => i.status === s)]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14 md:py-20" data-testid="mes-ceda">
      <p className="font-ui text-xs tracking-[0.3em] text-[#C9A24B] uppercase inline-flex items-center gap-2"><Leaf size={14} /> Mon espace</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#1F2A24] mt-3">Mes CEDA</h1>
      <p className="font-sans text-sm text-[#1F2A24]/65 mt-3 max-w-xl">Chaque capsule reste en attente jusqu'à ce que vous l'activiez. À l'activation, vous disposez de {data.active_days || 7} jours d'accès.</p>
      {data.items.length === 0 ? <Empty moduleActive={data.module_active} /> : groups.map(([s, list]) => list.length > 0 && (
        <section key={s} className="mt-10" data-testid={`mes-ceda-${s}`}>
          <h2 className="font-serif text-2xl text-[#1F2A24]">{{ active: "En cours", pending: "En attente d'activation", expired: "Terminées" }[s]}</h2>
          <div className="mt-4 space-y-4">
            {list.map((it) => <CedaItem key={it.id} it={it} open={open === it.id} onToggle={() => setOpen(open === it.id ? null : it.id)} onActivate={() => setConfirm(it)} moduleActive={data.module_active} />)}
          </div>
        </section>
      ))}
      {confirm && <ConfirmActivate it={confirm} onCancel={() => setConfirm(null)} onConfirm={() => activate(confirm)} />}
    </div>
  );
}

const Countdown = ({ expiresAt }) => {
  const [r, setR] = useState(remaining(expiresAt));
  useEffect(() => {
    const t = setInterval(() => setR(remaining(expiresAt)), 30000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (!r) return <span>Terminée</span>;
  return <span data-testid="ceda-countdown">{r.d} j {r.h} h {r.m} min restants</span>;
};

const CedaItem = ({ it, open, onToggle, onActivate, moduleActive }) => {
  const c = it.capsule;
  const tone = { active: "border-[#385E3A]", pending: "border-[#C9A24B]/60", expired: "border-[#E8DCC4] opacity-75" }[it.status];
  return (
    <article className={`rounded-3xl bg-white border-2 ${tone} overflow-hidden`} data-testid={`ceda-item-${it.id}`}>
      <div className="p-5 md:p-6 flex flex-wrap items-center gap-4">
        {c.cover_url ? <img src={`${BACKEND}${c.cover_url}`} alt="" className="w-16 h-16 rounded-2xl object-cover" /> : <span className="w-16 h-16 rounded-2xl bg-[#C9A24B]/15 text-[#C9A24B] flex items-center justify-center"><Sprout size={24} /></span>}
        <div className="flex-1 min-w-[180px]">
          <p className="font-ui text-[11px] tracking-[0.2em] text-[#C9A24B]">{c.code}</p>
          <h3 className="font-serif text-xl text-[#1F2A24]">{c.title}</h3>
          <p className="font-sans text-xs text-[#1F2A24]/55 mt-1 inline-flex items-center gap-1.5" data-testid={`ceda-item-status-${it.id}`}>
            {it.status === "active" && <><Hourglass size={12} /> <Countdown expiresAt={it.expires_at} /></>}
            {it.status === "pending" && <><Lock size={12} /> Non activée — vos 7 jours n'ont pas commencé</>}
            {it.status === "expired" && <>Terminée le {new Date(it.expires_at).toLocaleDateString("fr-FR")}</>}
          </p>
        </div>
        {it.status === "pending" && <button data-testid={`ceda-activate-${it.id}`} onClick={onActivate} className="rounded-full bg-[#C9A24B] text-[#1F2A24] px-6 py-3 font-ui text-sm hover:bg-[#b8923f] transition-colors">Activer</button>}
        {it.status === "active" && <button data-testid={`ceda-open-${it.id}`} onClick={onToggle} className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"><Play size={15} /> {open ? "Fermer" : "Pratiquer"}</button>}
        {it.status === "expired" && moduleActive && <Link to="/ceda" data-testid={`ceda-rebuy-${it.id}`} className="inline-flex items-center gap-2 rounded-full border border-[#C9A24B] px-5 py-2.5 font-ui text-sm"><RotateCcw size={14} /> Reprendre</Link>}
      </div>
      {open && it.status === "active" && <ActiveContent it={it} />}
    </article>
  );
};

const ActiveContent = ({ it }) => {
  const c = it.capsule;
  const src = `${API}/ceda/me/${it.id}/media`;
  return (
    <div className="border-t border-[#E8DCC4] p-5 md:p-7 space-y-6 animate-in fade-in duration-300" data-testid={`ceda-content-${it.id}`}>
      {c.has_media && (c.media_type === "audio"
        ? <audio data-testid="ceda-player" controls controlsList="nodownload" src={src} className="w-full" />
        : <video data-testid="ceda-player" controls controlsList="nodownload" playsInline src={src} className="w-full rounded-2xl bg-black aspect-video" />)}
      <CapsuleFiche c={c} />
      <CapsulePosologie c={c} />
      {c.integration_exercise && (
        <div className="rounded-2xl bg-[#1F2A24] text-[#F6F2E9] p-5" data-testid="ceda-exercise">
          <p className="font-ui text-[11px] tracking-[0.2em] text-[#C9A24B] uppercase">Exercice d'intégration</p>
          <p className="font-serif text-lg mt-2 whitespace-pre-line">{c.integration_exercise}</p>
        </div>
      )}
      {c.has_pdf && <a href={`${API}/ceda/me/${it.id}/pdf`} data-testid="ceda-pdf" className="inline-flex items-center gap-2 rounded-full border border-[#385E3A] text-[#385E3A] px-5 py-2.5 font-ui text-sm hover:bg-[#385E3A]/10"><FileDown size={15} /> Télécharger la fiche PDF</a>}
    </div>
  );
};

const ConfirmActivate = ({ it, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
    <div className="bg-white rounded-3xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="ceda-activate-modal">
      <Hourglass className="text-[#C9A24B]" size={28} />
      <h3 className="font-serif text-2xl text-[#1F2A24] mt-3">Activer {it.capsule.code} ?</h3>
      <p className="font-sans text-sm text-[#1F2A24]/70 mt-2">Vos 7 jours d'accès commencent maintenant et ne peuvent pas être mis en pause.</p>
      <div className="mt-6 flex justify-end gap-3">
        <button onClick={onCancel} className="rounded-full px-5 py-2.5 font-ui text-sm text-[#1F2A24]/70" data-testid="ceda-activate-cancel">Plus tard</button>
        <button onClick={onConfirm} className="rounded-full bg-[#385E3A] text-white px-6 py-2.5 font-ui text-sm hover:bg-[#2c4a2e]" data-testid="ceda-activate-confirm">Activer maintenant</button>
      </div>
    </div>
  </div>
);

const Empty = ({ moduleActive }) => (
  <div className="mt-10 rounded-3xl bg-white border border-[#E8DCC4] p-8 text-center" data-testid="mes-ceda-empty">
    <Sprout className="mx-auto text-[#C9A24B]" size={30} />
    <p className="font-serif text-xl text-[#1F2A24] mt-3">Aucune capsule pour l'instant</p>
    {moduleActive && <Link to="/ceda" data-testid="mes-ceda-discover" className="inline-block mt-5 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm">Découvrir les CEDA</Link>}
  </div>
);

const Gate = () => (
  <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="mes-ceda-gate">
    <Lock className="mx-auto text-[#C9A24B]" size={28} />
    <p className="font-serif text-2xl text-[#1F2A24] mt-3">Connectez-vous pour retrouver vos CEDA</p>
    <Link to="/connexion?next=/mon-espace/ceda" className="inline-block mt-5 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm">Se connecter</Link>
  </div>
);
